import { LockKeyhole, LogIn, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/api";
import { formatError, useAuth } from "@/auth";

export const LOGO_URL = "/airthermo-logo.png";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
);

const startGoogle = (next) => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = `${window.location.origin}/admin?next=${encodeURIComponent(next)}`;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login");
  const [sent, setSent] = useState(false);
  const handled = useRef(false);
  const next = params.get("next") || "/accueil";
  const sessionId = (location.hash.match(/session_id=([^&]+)/) || [])[1];

  useEffect(() => {
    if (!sessionId || handled.current) return;
    handled.current = true;
    window.history.replaceState(null, "", location.pathname + location.search);
    setBusy(true);
    api.post("/auth/google", { session_id: sessionId })
      .then(({ data }) => { setUser(data); navigate(next, { replace: true }); })
      .catch((err) => { setError(formatError(err.response?.data?.detail)); setBusy(false); });
  }, [sessionId, location.pathname, location.search, navigate, next, setUser]);

  if (user && !sessionId) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot", { email, app_url: window.location.origin });
        setSent(true);
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  return (
    <main className="login-page crm-login" data-testid="login-page">
      <div className="mesh" aria-hidden="true"><i className="mesh__a" /><i className="mesh__b" /><i className="mesh__c" /></div>
      <form className="login-card" onSubmit={submit}>
        <img src={LOGO_URL} alt="Airthermo" className="login-logo" />
        <p className="login-kicker"><LockKeyhole size={14} /> {mode === "forgot" ? "Mot de passe oublié" : "Espace CRM réservé"}</p>
        {mode === "login" && <>
          <button type="button" className="login-google" onClick={() => startGoogle(next)} disabled={busy} data-testid="login-google"><GoogleIcon />Continuer avec Google</button>
          <p className="login-or"><span>ou</span></p>
        </>}
        <label>E-mail<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></label>
        {mode === "login" && <label>Mot de passe<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></label>}
        {mode === "forgot" && <p className="login-hint">{sent ? "Si un compte existe pour cette adresse, un e-mail avec un lien pour choisir un nouveau mot de passe vient d'être envoyé (valable 2 heures)." : "Saisissez votre e-mail : vous recevrez un lien pour choisir un nouveau mot de passe."}</p>}
        {error && <p className="login-error" data-testid="login-error">{error}</p>}
        {!(mode === "forgot" && sent) && <button type="submit" className="login-submit" disabled={busy} data-testid="login-submit">{mode === "forgot" ? <><Mail size={16} />{busy ? "Envoi…" : "Recevoir le lien"}</> : <><LogIn size={16} />{busy ? "Connexion…" : "Se connecter"}</>}</button>}
        {mode === "login"
          ? <button type="button" className="login-link" onClick={() => switchMode("forgot")} data-testid="login-forgot">Mot de passe oublié ?</button>
          : <button type="button" className="login-link" onClick={() => switchMode("login")} data-testid="login-back-to-login">← Retour à la connexion</button>}
        <a href="/" className="login-back" data-testid="login-back-site">← Retour au site</a>
      </form>
    </main>
  );
}
